<template>
  <div class="editor-content px-4 py-4 readonly-html">
    <div
      v-if="isHTML(contentHtml)"
      class="ProseMirror tiptap w-full"
      :class="{
        'h-5': isTextEmpty(contentHtml),
        'is-editor-empty': isEmpty,
      }"
      :style="styleString"
      v-html="contentHtml"
    />
    <div v-else class="ProseMirror tiptap w-full" :style="styleString">
      <component
        :is="tagName"
        :class="{
          [mainClass]: true,
          'is-editor-empty ': isEmpty,
          'h-5': isTextEmpty(contentHtml),
        }"
        :style="styleString"
        v-html="contentHtml"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import './styles/main.css';
import type { JSONContent } from '@tiptap/vue-3';
import { generateHTML } from '@tiptap/core';
import { useRichTextEditorExtensions } from './composables/useExtensions';
import { isTextEmpty } from './composables/utils';
import {
  parseTooltip,
  countUnitsInHtml,
  isHTML,
} from './composables/TiptapTooltipUtils';
import { ref, watchEffect, computed, onMounted, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Object, String],
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  options: {
    type: Object,
    default: () => ({}),
  },
  extensions: {
    type: Object,
    default: () => ({}),
  },
});

const snapshotHTML = ref('');

const tagName = computed(() =>
  props.options.defaultNode === 'heading' ? 'h3' : 'p',
);

const mainClass = computed(() => {
  if (props.options.defaultNode === 'heading') return 'title_md';
  if (
    props.options.defaultNode === 'headline' &&
    props.options.sizeBreakpoint >
      countUnitsInHtml(props.modelValue, { includeKatex: false })
  )
    return 'headlie-title_md whitespace-normal w-full';
  return 'body whitespace-normal w-full';
});

const isEmpty = computed(() => isTextEmpty(snapshotHTML.value));

const contentHtml = computed(() =>
  isEmpty.value ? parseTooltip(props.placeholder) : snapshotHTML.value,
);

const styleString = computed(
  () =>
    `text-align: ${props.options.textAlign}; font-size: ${props.options.fontSize ?? '14px'}; line-height: ${props.options.lineHeight}`,
);

watchEffect(() => {
  if (props.modelValue) {
    if (typeof props.modelValue === 'object') {
      snapshotHTML.value = generateHTML(
        props.modelValue as JSONContent,
        useRichTextEditorExtensions(props.extensions),
      );
    } else if (typeof props.modelValue === 'string') {
      snapshotHTML.value = parseTooltip(props.modelValue);
    }
  }
});
</script>

<style>
.tiptap-placeholder * {
  @apply text-secondary-600;
}
.headlie-title_md label {
  @apply title_md;
}
</style>
